import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { ReactComponent as ArrowDropUpIcon } from "../icons/ShortIcon.svg";
import { ReactComponent as ArrowDropDownIcon } from "../icons/ShortIconDown.svg";
import { orHeatMapWeek } from "Samarth Module/api/orltAPI";
import { SelectedAlertsContext } from "utils/SelectedAlertContext";

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "Unilever Shilling",
  fontSize: "10px",
  textAlign: "center",
  whiteSpace: "normal",
  lineHeight: 1.1,
  padding: "4px 6px",
  border: "none",
  minWidth: "70px",
  maxWidth: "90px",
  "&.MuiTableCell-head": {
    fontWeight: 700,
    backgroundColor: "#fff",
    color: "#000",
  },
  "&.MuiTableCell-body": {
    fontWeight: 400,
  },
}));

const StickyFirstColumnHead = styled(StyledTableCell)(() => ({
  position: "sticky",
  left: 0,
  backgroundColor: "#fff",
  zIndex: 5,
  fontWeight: 700,
  color: "#354ACD",
}));

const StickyFirstColumnCell = styled(StyledTableCell)(() => ({
  position: "sticky",
  left: 0,
  backgroundColor: "#fff",
  zIndex: 4,
  fontWeight: 700,
  color: "#354ACD",
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: "#fff !important",
}));

const getBGColor = (value, show) => {
  if (!show) {
    return { backgroundColor: "#e1dadac0" }
  }
  if (value === Infinity || (+value) > 90) return { backgroundColor: "#A3E1D2" };
  if (value >= 75) return { backgroundColor: "#FFEC9A" };
  return { backgroundColor: "#FCA5A5" };

};

// above 90 - green 
// 90 to 75 - Amber 
// below 75 red 

const HeatMapTable = ({tblView, groupingSelection, selectionType}) => {

  const [heatMapData, setHeatMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lossSelection] = useState("Loss 1");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dynamicKeys, setDynamicKeys] = useState([]);
  const { orLossTreeSelectedFilters } = useContext(SelectedAlertsContext);

  const transformData = (data, grouping) => {
    const keysSet = new Set();
    const transformed = data.map((row) => {
      const label = row[grouping] || "N/A";
      const values = row.Week_data.map((obj) => {
        keysSet.add(obj.CalendarWeek);
        return {
          key: obj.CalendarWeek,
          label: obj.CalendarWeek,
          value: obj?.OR_Percentage ?? 0,
          display: obj?.display ?? true,
        };
      });

      return { label, values };
    });
    const keys = Array.from(keysSet);
    const sortedKeys = keys.filter((k) => k !== "Total");
    if (keys.includes("Total")) sortedKeys.push("Total");

    // const today = new Date();
    // const day = today.getDay();

    // Show current week data from wednesday onwards because pipeline refresh on wednesday
    // if (day === 1 || day === 2) {
    //   sortedKeys.pop();
    // }

    setDynamicKeys(sortedKeys);

    // Move Total row to the top
    const totalRow = transformed.find((r) => r.label === "Total");
    const rest = transformed.filter((r) => r.label !== "Total");
    return totalRow ? [totalRow, ...rest] : [...rest];
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setHeatMapData([])
        const response = await orHeatMapWeek(
          groupingSelection,
          lossSelection,
          orLossTreeSelectedFilters,
          tblView,
          selectionType
        );

        if (isMounted) {
          const transformed = transformData(
            (Array.isArray(response) ? response : []),
            groupingSelection
          );
          setHeatMapData(transformed);
          setSortColumn(null);
        }
      } catch (err) {
        console.error("OR Trends Week Heat map  error", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupingSelection, lossSelection, JSON.stringify(orLossTreeSelectedFilters), tblView, selectionType]);

  const handleSort = (key) => {
    const order = sortColumn === key && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(key);
    setSortOrder(order);

    // Separate the "Total" row from other rows
    const totalRow = heatMapData.find((row) => row.label === "Total");
    const dataWithoutTotal = heatMapData.filter((row) => row.label !== "Total");

    // Sort only the non-Total rows
    const sorted = [...dataWithoutTotal].sort((a, b) => {
      const valA = a.values.find((v) => v.key === key)?.value ?? 0;
      const valB = b.values.find((v) => v.key === key)?.value ?? 0;
      return order === "asc" ? valA - valB : valB - valA;
    });

    // Add the Total row back at the top
    if (totalRow) {
      sorted.unshift(totalRow);
    }

    setHeatMapData(sorted);
  };

  const chartHeight = () => {
    const mediaQuery1 = window.matchMedia("(max-width: 1300px)").matches;
    const mediaQuery2 = window.matchMedia("(max-width: 1450px)").matches;
    const mediaQuery3 = window.matchMedia("(max-width: 1550px)").matches;
    return mediaQuery1 ? 216 : mediaQuery2 ? 240 : mediaQuery3 ? 275 : 300
  }

  useEffect(() => {
    const scrollableDiv = document.getElementById('or_trend_chart');
    if (scrollableDiv && heatMapData && !loading) {
      scrollableDiv.scrollTo({
        top: 0,
        left: scrollableDiv.scrollWidth,
        behavior: "smooth"
      });
    }
  }, [heatMapData, loading]);

  const isNoData = useMemo(() => {
    if (!Array.isArray(heatMapData)) return;
    return (heatMapData.length === 1 && !(heatMapData[0]?.values.find((obj) => obj.value !== 0))) || heatMapData.length === 0
  }, [heatMapData])

  return (
    <Box sx={{
      p: 0,
      borderRadius: 2,
      width: "100%",
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column",
      height: "calc(37vh + 40px)", // Total height = table + dropdown + footer
    }}>

      {/* Table container - exactly 37vh */}
      <Box sx={{
        height: "37vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : isNoData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: '18px'
            }}
          >
            Data is not available
          </Box>
        ) : (
          <Box id="or_trend_chart"
            sx={{ 
              maxWidth: "100%", 
              maxHeight: chartHeight(), 
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              }
            }}>
            <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <StickyFirstColumnHead sx={{minWidth : groupingSelection === 'CBU' ? '13rem' : '100px'}}>
                      {groupingSelection}
                    </StickyFirstColumnHead>
                    {dynamicKeys.map((key, idx) => (
                      <StyledTableCell key={idx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.2,
                          }}
                        >
                          <Box>{key}</Box>
                          <IconButton
                            size='small'
                            onClick={() => handleSort(key)}
                            sx={{ padding: 0, marginLeft: "2px", color: "#000" }}
                          >
                            {sortColumn === key ? (
                              sortOrder === "asc" ? (
                                <ArrowDropUpIcon fontSize='small' />
                              ) : (
                                <ArrowDropDownIcon fontSize='small' />
                              )
                            ) : (
                              <ArrowDropUpIcon
                                fontSize='small'
                                sx={{ opacity: 0.3 }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heatMapData.map((row, idx) => {
                    const isTotalRow = row.label === "Total";
                    const stickyStyle = isTotalRow
                      ? {
                          position: "sticky",
                          top: 29,
                          zIndex: 3,
                          backgroundColor: "#f0f4ff",
                        }
                      : {};
                    return (
                      <StyledTableRow key={idx}>
                        <StickyFirstColumnCell
                          sx={{
                            ...(isTotalRow
                              ? {
                                  position: "sticky",
                                  top: 29,
                                  left: 0,
                                  zIndex: 6,
                                  backgroundColor: "#f0f4ff",
                                  fontWeight: 700,
                                }
                              : {}),
                          }}
                        >
                          {row.label}
                        </StickyFirstColumnCell>
                        {dynamicKeys.map((key, colIdx) => {
                          const valObj = row.values.find((v) => v.key === key);
                          const val = valObj?.value ?? 0;
                          return (
                            <StyledTableCell key={colIdx} sx={stickyStyle}>
                                <div
                                  style={{
                                    ...getBGColor(val, valObj?.display),
                                    borderRadius: "3px",
                                    padding: "1px 0",
                                    fontSize: "10px",
                                    color: !valObj?.display ? "#e1dadac0" : "#000",
                                  }}
                                >
                                  {val === Infinity
                                    ? "∞"
                                    : typeof val === "number" && val > 0
                                      ? `${Math.round(val)}%`
                                      : "0%"}
                                </div>
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
        )}
      </Box>

      {/* Footer section - fixed height */}
      {!isNoData && <Box sx={{
        borderTop: "1px solid #EAECF0",
        pt: 1,
        pb: 0,
        height: "28px",
        flexShrink: 0
      }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <LegendItem label='>=90%' color='#A3E1D2' />
          <LegendItem label='90 - 75 %' color='#FFEC9A' />
          <LegendItem label='<75%' color='#FCA5A5' />
        </Box>
      </Box>}
    </Box>
  );
};

const LegendItem = ({ label, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Box
      component='span'
      sx={{
        width: "2px",
        height: "12px",
        backgroundColor: color,
        display: "inline-block",
        borderRadius: "1px",
      }}
    />
    <Typography sx={{ fontSize: "11px", color: "#000" }}>{label}</Typography>
  </Box>
);

export default HeatMapTable;